import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { db, storage } from '../firebase';
import { doc, getDoc, updateDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { DIFFICULTY_LEVELS, PREFECTURES, VALIDATION_RULES } from '../utils/constants';
import { validateMaxLength, validateParticipants } from '../utils/helpers';
import { compressImageFile } from '../utils/imageHelpers';
import { guardContentSubmission, showModerationWarning } from '../utils/moderation';
import { showToast } from '../utils/toast';
import './EditRecruitment.css';

const GENDER_RATIO_OPTIONS = [
  '未設定',
  '男性多め',
  '女性多め',
  '半々くらい',
  '男性のみ',
  '女性のみ',
  'その他'
];

const TRIP_TYPE_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'day_hike', label: '日帰り' },
  { value: 'hut_stay', label: '小屋泊' },
  { value: 'tent_stay', label: 'テント泊' },
  { value: 'multi_day', label: '縦走・複数日' }
];

const TRANSPORT_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'public_transport', label: '公共交通' },
  { value: 'car', label: '車（レンタカー）' },
  { value: 'carpool', label: '車（相乗り）' },
  { value: 'mixed', label: '公共交通・車どちらも可' }
];

const DANGER_OPTIONS = [
  { value: 'chain', label: '鎖場' },
  { value: 'rock', label: '岩場' },
  { value: 'snow', label: '雪渓・残雪' },
  { value: 'river_crossing', label: '渡渉' },
  { value: 'steep_descent', label: '急登・急下降' }
];

const EQUIPMENT_REQUIREMENT_OPTIONS = [
  { value: 'helmet', label: 'ヘルメット' },
  { value: 'chain_spike', label: 'チェーンスパイク' },
  { value: 'trekking_pole', label: 'トレッキングポール' },
  { value: 'crampon', label: 'アイゼン' },
  { value: 'first_aid', label: 'ファーストエイドキット' },
  { value: 'hardshell', label: 'ハードシェル' },
  { value: 'avalanche_gear', label: '雪崩装備' }
];

const MEETING_AREA_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'station', label: '駅集合' },
  { value: 'trailhead', label: '登山口集合' },
  { value: 'local', label: '現地集合' },
  { value: 'other', label: 'その他' }
];

const EXPERIENCE_LEVEL_TARGET_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'beginner_welcome', label: '初心者歓迎' },
  { value: 'beginner', label: '初級者向け' },
  { value: 'intermediate', label: '中級者向け' },
  { value: 'advanced', label: '上級者向け' }
];

const CAR_SHARE_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'available', label: '相乗り可' },
  { value: 'not_available', label: '相乗り不可' },
  { value: 'consult', label: '相談可' }
];

const WEATHER_THRESHOLD_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'light_rain_ok', label: '小雨決行' },
  { value: 'rain_cancel', label: '雨天中止' },
  { value: 'storm_cancel', label: '荒天中止' },
  { value: 'decide_previous_day', label: '前日判断' }
];

const ESTIMATED_BUDGET_OPTIONS = [
  { value: '', label: '選択しない' },
  { value: 'under_3000', label: '3,000円未満' },
  { value: '3000_5000', label: '3,000〜5,000円' },
  { value: '5000_10000', label: '5,000〜10,000円' },
  { value: '10000_20000', label: '10,000〜20,000円' },
  { value: 'over_20000', label: '20,000円以上' }
];

const isBeginnerDangerous = (difficulty, dangerFlags = []) => {
  return difficulty === 'advanced' || (Array.isArray(dangerFlags) && dangerFlags.length > 0);
};

const EMPTY_FORM_DATA = {
  mountainName: '',
  route: '',
  departureDate: '',
  maxParticipants: 1,
  difficulty: 'beginner',
  description: '',
  prefecture: '',
  meetingPoint: '',
  equipment: '',
  requirements: '',
  status: 'active',
  eventName: '',
  checklistUrl: '',
  tripType: '',
  transportMode: '',
  walkingHours: '',
  elevationGain: '',
  dangerFlags: [],
  equipmentRequirements: [],
  meetingAreaType: '',
  experienceLevelTarget: '',
  carShareAvailable: '',
  weatherThreshold: '',
  estimatedBudgetBand: '',
  safetyAcknowledged: false,
  minParticipants: 3,
  genderRatioSingle: '',
  participantCondition: 'anyone',
  groupName: '',
  activityFrequency: '',
  activityFrequencyOther: '',
  currentMembers: 1,
  genderRatio: '',
  ageGroup: '',
  activityStyle: '',
  homepage: ''
};

function formatDateInputValue(value) {
  if (!value) return '';
  const date = value.toDate ? value.toDate() : new Date(value);
  if (Number.isNaN(date.getTime())) return '';
  return date.toISOString().slice(0, 10);
}

function EditRecruitment() {
  const { id } = useParams();
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [recruitmentImageFile, setRecruitmentImageFile] = useState(null);
  const [recruitmentImagePreview, setRecruitmentImagePreview] = useState('');
  const [existingImageUrl, setExistingImageUrl] = useState('');
  const [recruitmentType, setRecruitmentType] = useState('single');
  const imageInputRef = useRef(null);
  const [formData, setFormData] = useState(EMPTY_FORM_DATA);

  useEffect(() => {
    const fetchRecruitment = async () => {
      try {
        const recruitmentRef = doc(db, 'recruitments', id);
        const recruitmentSnap = await getDoc(recruitmentRef);

        if (!recruitmentSnap.exists()) {
          setError('募集が見つかりません');
          return;
        }

        const recruitmentData = recruitmentSnap.data();

        if (!currentUser?.uid || currentUser.uid !== recruitmentData.userId) {
          setError('この募集を編集する権限がありません');
          return;
        }

        const type = recruitmentData.recruitmentType || 'single';
        setRecruitmentType(type);

        if (recruitmentData.imageUrl) {
          setExistingImageUrl(recruitmentData.imageUrl);
          setRecruitmentImagePreview(recruitmentData.imageUrl);
        }

        setFormData({
          ...EMPTY_FORM_DATA,
          mountainName: recruitmentData.mountainName || '',
          route: recruitmentData.route || '',
          departureDate: formatDateInputValue(recruitmentData.departureDate),
          maxParticipants: recruitmentData.maxParticipants || 1,
          difficulty: recruitmentData.difficulty || 'beginner',
          description: recruitmentData.description || '',
          prefecture: recruitmentData.prefecture || '',
          meetingPoint: recruitmentData.meetingPoint || '',
          equipment: recruitmentData.equipment || '',
          requirements: recruitmentData.requirements || '',
          status: recruitmentData.status || 'active',
          eventName: recruitmentData.eventName || '',
          checklistUrl: recruitmentData.checklistUrl || '',
          tripType: recruitmentData.tripType || '',
          transportMode: recruitmentData.transportMode || '',
          walkingHours: recruitmentData.walkingHours ?? '',
          elevationGain: recruitmentData.elevationGain ?? '',
          dangerFlags: Array.isArray(recruitmentData.dangerFlags) ? recruitmentData.dangerFlags : [],
          equipmentRequirements: Array.isArray(recruitmentData.equipmentRequirements) ? recruitmentData.equipmentRequirements : [],
          meetingAreaType: recruitmentData.meetingAreaType || '',
          experienceLevelTarget: recruitmentData.experienceLevelTarget || '',
          carShareAvailable: recruitmentData.carShareAvailable || '',
          weatherThreshold: recruitmentData.weatherThreshold || '',
          estimatedBudgetBand: recruitmentData.estimatedBudgetBand || '',
          minParticipants: recruitmentData.minParticipants || 3,
          genderRatioSingle: recruitmentData.genderRatioSingle || '',
          participantCondition: recruitmentData.participantCondition || 'anyone',
          groupName: recruitmentData.groupName || '',
          activityFrequency: recruitmentData.activityFrequency || '',
          activityFrequencyOther: recruitmentData.activityFrequencyOther || '',
          currentMembers: recruitmentData.currentMembers || 1,
          genderRatio: recruitmentData.genderRatio || '',
          ageGroup: recruitmentData.ageGroup || '',
          activityStyle: recruitmentData.activityStyle || '',
          homepage: recruitmentData.homepage || '',
          safetyAcknowledged: recruitmentData.safetyAcknowledged ?? true
        });
      } catch (fetchError) {
        console.error('募集取得エラー:', fetchError);
        setError('募集の取得に失敗しました');
      } finally {
        setLoading(false);
      }
    };

    fetchRecruitment();
  }, [currentUser, id]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : type === 'number' ? (value === '' ? '' : Number(value)) : value
    }));
  };

  const handleMultiSelectChange = (name, value) => {
    setFormData(prev => {
      const currentValues = Array.isArray(prev[name]) ? prev[name] : [];
      const nextValues = currentValues.includes(value)
        ? currentValues.filter(item => item !== value)
        : [...currentValues, value];
      return {
        ...prev,
        [name]: nextValues
      };
    });
  };

  const handleActivityFrequencyChange = (e) => {
    const value = e.target.value;
    setFormData((prev) => ({
      ...prev,
      activityFrequency: value,
      activityFrequencyOther: value === 'other' ? prev.activityFrequencyOther : ''
    }));
  };

  const handleImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 10 * 1024 * 1024) {
      setError('画像は10MB以下にしてください。');
      return;
    }

    try {
      setError('');
      const compressedFile = await compressImageFile(file, {
        maxWidth: 1280,
        maxHeight: 1280,
        quality: 0.85,
        outputType: 'image/jpeg'
      });
      setRecruitmentImageFile(compressedFile);
      setRecruitmentImagePreview(URL.createObjectURL(compressedFile));
    } catch (compressionError) {
      console.error('画像圧縮エラー:', compressionError);
      setError('画像の圧縮に失敗しました。別の画像で再試行してください。');
    }
  };

  const validateSingleRecruitment = () => {
    if (!formData.mountainName.trim()) return '山名を入力してください。';
    if (!formData.departureDate) return '出発日を選択してください。';
    if (!formData.prefecture) return '都道府県を選択してください。';
    if (formData.walkingHours === '' || formData.walkingHours === null || Number(formData.walkingHours) <= 0) {
      return '標準CTを入力してください。';
    }
    if (!formData.safetyAcknowledged) {
      return '募集者が計画責任者であること、登山には危険があり参加判断は各自の自己責任で行うこと、登山計画書の提出を忘れずに案内することを確認してください。';
    }
    const mountainNameValidation = validateMaxLength(
      formData.mountainName,
      VALIDATION_RULES.MOUNTAIN_NAME_MAX_LENGTH,
      '山名'
    );
    if (!mountainNameValidation.isValid) return mountainNameValidation.message;

    const routeValidation = validateMaxLength(
      formData.route,
      VALIDATION_RULES.ROUTE_MAX_LENGTH,
      'ルート'
    );
    if (!routeValidation.isValid) return routeValidation.message;

    const meetingPointValidation = validateMaxLength(
      formData.meetingPoint,
      VALIDATION_RULES.MEETING_POINT_MAX_LENGTH,
      '集合場所'
    );
    if (!meetingPointValidation.isValid) return meetingPointValidation.message;

    const equipmentValidation = validateMaxLength(
      formData.equipment,
      VALIDATION_RULES.EQUIPMENT_MAX_LENGTH,
      '装備'
    );
    if (!equipmentValidation.isValid) return equipmentValidation.message;

    const participantsValidation = validateParticipants(formData.maxParticipants);
    if (!participantsValidation.isValid) return participantsValidation.message;

    return '';
  };

  const validateGroupRecruitment = () => {
    if (!formData.groupName.trim()) return '団体名を入力してください。';
    if (!formData.prefecture) return '都道府県を選択してください。';
    if (!formData.activityFrequency) return '活動頻度を選択してください。';
    if (formData.activityFrequency === 'other' && !formData.activityFrequencyOther.trim()) {
      return '活動頻度（その他）を入力してください。';
    }
    if (!formData.activityStyle) return '活動スタイルを選択してください。';
    if (!formData.description.trim()) return '詳細説明を入力してください。';
    if (!formData.currentMembers || formData.currentMembers < 1) {
      return '現在のメンバー数は1以上で入力してください。';
    }
    return '';
  };

  const buildUpdatePayload = (imageUrl) => {
    const basePayload = {
      recruitmentType,
      description: formData.description,
      prefecture: formData.prefecture,
      status: formData.status,
      imageUrl,
      updatedAt: serverTimestamp()
    };

    if (recruitmentType === 'group') {
      return {
        ...basePayload,
        groupName: formData.groupName,
        activityFrequency: formData.activityFrequency,
        activityFrequencyOther: formData.activityFrequencyOther,
        currentMembers: formData.currentMembers,
        genderRatio: formData.genderRatio,
        ageGroup: formData.ageGroup,
        activityStyle: formData.activityStyle,
        homepage: formData.homepage,
        mountainName: '',
        route: '',
        departureDate: null,
        maxParticipants: 1,
        difficulty: 'beginner',
        meetingPoint: '',
        equipment: '',
        requirements: '',
        eventName: '',
        checklistUrl: '',
        tripType: '',
        dayTripOrStay: '',
        transportMode: '',
        walkingHours: '',
        elevationGain: '',
        dangerFlags: [],
        equipmentRequirements: [],
        meetingAreaType: '',
        experienceLevelTarget: '',
        carShareAvailable: '',
        weatherThreshold: '',
        estimatedBudgetBand: '',
        minParticipants: 3,
        genderRatioSingle: '',
        participantCondition: 'anyone'
      };
    }

    return {
      ...basePayload,
      mountainName: formData.mountainName,
      route: formData.route,
      departureDate: new Date(formData.departureDate),
      maxParticipants: formData.maxParticipants,
      difficulty: formData.difficulty,
      meetingPoint: formData.meetingPoint,
      equipment: formData.equipment,
      requirements: formData.requirements,
      eventName: formData.eventName,
      checklistUrl: formData.checklistUrl,
      tripType: formData.tripType,
      dayTripOrStay: formData.tripType,
      transportMode: formData.transportMode,
      walkingHours: formData.walkingHours,
      elevationGain: formData.elevationGain,
      dangerFlags: formData.dangerFlags,
      equipmentRequirements: formData.equipmentRequirements,
      meetingAreaType: formData.meetingAreaType,
      experienceLevelTarget: formData.experienceLevelTarget,
      carShareAvailable: formData.carShareAvailable,
      weatherThreshold: formData.weatherThreshold,
      estimatedBudgetBand: formData.estimatedBudgetBand,
      minParticipants: formData.minParticipants,
      genderRatioSingle: formData.genderRatioSingle,
      participantCondition: formData.participantCondition,
      groupName: '',
      activityFrequency: '',
      activityFrequencyOther: '',
      currentMembers: 1,
      genderRatio: '',
      ageGroup: '',
      activityStyle: '',
      homepage: ''
    };
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    const validationError =
      recruitmentType === 'group' ? validateGroupRecruitment() : validateSingleRecruitment();
    if (validationError) {
      setError(validationError);
      return;
    }

    const descriptionValidation = validateMaxLength(
      formData.description,
      VALIDATION_RULES.DESCRIPTION_MAX_LENGTH,
      '詳細'
    );
    if (!descriptionValidation.isValid) {
      setError(descriptionValidation.message);
      return;
    }

    setSubmitting(true);
    try {
      const recruitmentRef = doc(db, 'recruitments', id);
      const moderation = await guardContentSubmission({
        action: 'recruitment_update',
        contentType: recruitmentType === 'group' ? 'group_recruitment' : 'single_recruitment',
        texts: [
          formData.mountainName,
          formData.route,
          formData.description,
          formData.meetingPoint,
          formData.equipment,
          formData.eventName,
          formData.groupName,
          formData.activityFrequencyOther,
        ],
        metadata: {
          recruitmentId: id,
          recruitmentType,
        },
      });
      showModerationWarning(moderation);

      let imageUrl = existingImageUrl;
      if (recruitmentImageFile) {
        const storageRef = ref(storage, `recruitment_images/${currentUser.uid}_${Date.now()}`);
        await uploadBytes(storageRef, recruitmentImageFile, {
          contentType: recruitmentImageFile.type || 'image/jpeg',
        });
        imageUrl = await getDownloadURL(storageRef);
      }

      await updateDoc(recruitmentRef, buildUpdatePayload(imageUrl));

      showToast({ message: '募集を更新しました！', type: 'success' });
      navigate(`/recruitments/${id}`);
    } catch (submitError) {
      console.error('募集更新エラー:', submitError);
      setError('募集の更新に失敗しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="loading">読み込み中...</div>;
  }

  if (error && !formData.description && !formData.groupName && !formData.mountainName) {
    return (
      <div className="error-container">
        <div className="error-message">{error}</div>
        <button onClick={() => navigate('/recruitments')} className="btn btn-outline">
          募集一覧に戻る
        </button>
      </div>
    );
  }

  return (
    <div className="edit-recruitment-container">
      {submitting && (
        <div className="loading-overlay">
          <div className="loading-spinner" />
          保存中...
        </div>
      )}
      <div className="edit-header">
        <h1>募集を編集</h1>
        <Link to={`/recruitments/${id}`} className="btn btn-outline">
          詳細に戻る
        </Link>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="edit-form">
        <form onSubmit={handleSubmit}>
          {recruitmentType === 'group' ? (
            <>
              <div className="form-group">
                <label className="form-label">団体名 *</label>
                <input
                  type="text"
                  name="groupName"
                  className="form-control"
                  value={formData.groupName}
                  onChange={handleChange}
                  required
                  placeholder="例: 東京山岳会"
                />
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">都道府県 *</label>
                  <select
                    name="prefecture"
                    className="form-control"
                    value={formData.prefecture}
                    onChange={handleChange}
                    required
                  >
                    <option value="">選択してください</option>
                    {PREFECTURES.map((prefecture) => (
                      <option key={prefecture} value={prefecture}>{prefecture}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">募集状況 *</label>
                  <select
                    name="status"
                    className="form-control"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="active">募集中</option>
                    <option value="closed">募集終了</option>
                    <option value="cancelled">中止</option>
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">活動頻度 *</label>
                  <select
                    name="activityFrequency"
                    className="form-control"
                    value={formData.activityFrequency}
                    onChange={handleActivityFrequencyChange}
                    required
                  >
                    <option value="">選択してください</option>
                    <option value="weekly">週1回</option>
                    <option value="biweekly">月2回</option>
                    <option value="monthly">月1回</option>
                    <option value="seasonal">季節ごと</option>
                    <option value="irregular">不定期</option>
                    <option value="other">その他</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">現在のメンバー数 *</label>
                  <input
                    type="number"
                    name="currentMembers"
                    className="form-control"
                    value={formData.currentMembers}
                    onChange={handleChange}
                    required
                    min="1"
                  />
                </div>
              </div>

              {formData.activityFrequency === 'other' && (
                <div className="form-group">
                  <label className="form-label">活動頻度（その他）</label>
                  <input
                    type="text"
                    name="activityFrequencyOther"
                    className="form-control"
                    value={formData.activityFrequencyOther}
                    onChange={handleChange}
                    placeholder="例: 年10回程度、土日のみなど"
                  />
                </div>
              )}

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">男女比</label>
                  <select
                    name="genderRatio"
                    className="form-control"
                    value={formData.genderRatio}
                    onChange={handleChange}
                  >
                    {GENDER_RATIO_OPTIONS.map((option) => (
                      <option key={option} value={option === '未設定' ? '' : option}>
                        {option}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">主な年齢層</label>
                  <select
                    name="ageGroup"
                    className="form-control"
                    value={formData.ageGroup}
                    onChange={handleChange}
                  >
                    <option value="">選択してください</option>
                    <option value="20s">20代</option>
                    <option value="30s">30代</option>
                    <option value="40s">40代</option>
                    <option value="50s">50代</option>
                    <option value="60s">60代以上</option>
                    <option value="mixed">幅広い年齢層</option>
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">活動スタイル *</label>
                <select
                  name="activityStyle"
                  className="form-control"
                  value={formData.activityStyle}
                  onChange={handleChange}
                  required
                >
                  <option value="">選択してください</option>
                  <option value="beginner-friendly">初心者歓迎</option>
                  <option value="fun-oriented">楽しさ重視</option>
                  <option value="satoyama-focused">里山中心</option>
                  <option value="hyakumeizan-focused">百名山中心</option>
                  <option value="lightweight-oriented">軽量志向</option>
                  <option value="senior-friendly">シニア向け</option>
                  <option value="mixed">様々なスタイル</option>
                </select>
              </div>

              <div className="form-group">
                <label className="form-label">ホームページ</label>
                <input
                  type="url"
                  name="homepage"
                  className="form-control"
                  value={formData.homepage}
                  onChange={handleChange}
                  placeholder="https://example.com"
                />
              </div>
            </>
          ) : (
            <>
              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">山名 *</label>
                  <input
                    type="text"
                    name="mountainName"
                    className="form-control"
                    value={formData.mountainName}
                    onChange={handleChange}
                    required
                    placeholder="例: 富士山"
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">登山ルート</label>
                  <input
                    type="text"
                    name="route"
                    className="form-control"
                    value={formData.route}
                    onChange={handleChange}
                    placeholder="例: 吉田ルート"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">出発予定日 *</label>
                  <input
                    type="date"
                    name="departureDate"
                    className="form-control"
                    value={formData.departureDate}
                    onChange={handleChange}
                    required
                  />
                </div>

                <div className="form-group">
                  <label className="form-label">募集人数 *</label>
                  <input
                    type="number"
                    name="maxParticipants"
                    className="form-control"
                    value={formData.maxParticipants}
                    onChange={handleChange}
                    required
                    min="1"
                    max="20"
                  />
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">難易度 *</label>
                  <select
                    name="difficulty"
                    className="form-control"
                    value={formData.difficulty}
                    onChange={handleChange}
                    required
                  >
                    {Object.values(DIFFICULTY_LEVELS).map(({ id: difficultyId, label }) => (
                      <option key={difficultyId} value={difficultyId}>{label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">都道府県 *</label>
                  <select
                    name="prefecture"
                    className="form-control"
                    value={formData.prefecture}
                    onChange={handleChange}
                    required
                  >
                    <option value="">選択してください</option>
                    {PREFECTURES.map((prefecture) => (
                      <option key={prefecture} value={prefecture}>{prefecture}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">募集状況 *</label>
                  <select
                    name="status"
                    className="form-control"
                    value={formData.status}
                    onChange={handleChange}
                    required
                  >
                    <option value="active">募集中</option>
                    <option value="closed">募集終了</option>
                    <option value="cancelled">中止</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">集合場所・時間</label>
                  <input
                    type="text"
                    name="meetingPoint"
                    className="form-control"
                    value={formData.meetingPoint}
                    onChange={handleChange}
                    placeholder="例: 新宿駅7:00"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">持参装備</label>
                <textarea
                  name="equipment"
                  className="form-control"
                  value={formData.equipment}
                  onChange={handleChange}
                  placeholder="例: 登山靴、ヘルメット、雨具、防寒着"
                  rows="3"
                />
              </div>

              <div className="safety-notice">
                <label className="checkbox-label">
                  <input
                    type="checkbox"
                    name="safetyAcknowledged"
                    checked={formData.safetyAcknowledged}
                    onChange={handleChange}
                    required
                  />
                  募集者が計画責任者であること、登山には危険があり参加判断は各自の自己責任で行うこと、登山計画書の提出を忘れずに案内することを確認しました。
                </label>
                {isBeginnerDangerous(formData.difficulty, formData.dangerFlags) && (
                  <p className="form-help" style={{ color: 'var(--ember)', fontWeight: 700, margin: '8px 0 0' }}>
                    この山は初心者には危険です。募集文に必要経験・危険箇所・撤退判断を明記してください。
                  </p>
                )}
              </div>


              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">山行タイプ（任意）</label>
                  <select
                    name="tripType"
                    className="form-control"
                    value={formData.tripType}
                    onChange={handleChange}
                  >
                    {TRIP_TYPE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">主な移動手段（任意）</label>
                  <select
                    name="transportMode"
                    className="form-control"
                    value={formData.transportMode}
                    onChange={handleChange}
                  >
                    {TRANSPORT_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">集合場所タイプ（任意）</label>
                  <select
                    name="meetingAreaType"
                    className="form-control"
                    value={formData.meetingAreaType}
                    onChange={handleChange}
                  >
                    {MEETING_AREA_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">対象経験レベル（任意）</label>
                  <select
                    name="experienceLevelTarget"
                    className="form-control"
                    value={formData.experienceLevelTarget}
                    onChange={handleChange}
                  >
                    {EXPERIENCE_LEVEL_TARGET_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">相乗り可否（任意）</label>
                  <select
                    name="carShareAvailable"
                    className="form-control"
                    value={formData.carShareAvailable}
                    onChange={handleChange}
                  >
                    {CAR_SHARE_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">天候判断（任意）</label>
                  <select
                    name="weatherThreshold"
                    className="form-control"
                    value={formData.weatherThreshold}
                    onChange={handleChange}
                  >
                    {WEATHER_THRESHOLD_OPTIONS.map(option => (
                      <option key={option.value} value={option.value}>{option.label}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">想定予算（任意）</label>
                <select
                  name="estimatedBudgetBand"
                  className="form-control"
                  value={formData.estimatedBudgetBand}
                  onChange={handleChange}
                >
                  {ESTIMATED_BUDGET_OPTIONS.map(option => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </select>
              </div>

              <div className="form-row">
                <div className="form-group">
                  <label className="form-label">
                    標準CT *
                    <span
                      className="tooltip-icon"
                      tabIndex="0"
                      role="note"
                      data-tooltip="標準コースタイムは、休憩を除いた一般的な歩行時間の目安です。山と高原地図・YAMAP等のコースタイムを参考に、時間単位で入力してください。"
                      title="標準コースタイムの説明"
                    >
                      ?
                    </span>
                  </label>
                  <input
                    type="number"
                    name="walkingHours"
                    className="form-control"
                    value={formData.walkingHours}
                    onChange={handleChange}
                    min="0"
                    step="0.5"
                    placeholder="例: 5.5"
                    required
                  />
                  <p className="form-help">標準コースタイムを時間で入力してください。</p>
                </div>

                <div className="form-group">
                  <label className="form-label">標高差目安 m（任意）</label>
                  <input
                    type="number"
                    name="elevationGain"
                    className="form-control"
                    value={formData.elevationGain}
                    onChange={handleChange}
                    min="0"
                    step="50"
                    placeholder="例: 800"
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">危険箇所・注意地形（任意）</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {DANGER_OPTIONS.map(option => (
                    <label key={option.value} className="checkbox-label" style={{ width: 'auto' }}>
                      <input
                        type="checkbox"
                        checked={formData.dangerFlags.includes(option.value)}
                        onChange={() => handleMultiSelectChange('dangerFlags', option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
              </div>

              <div className="form-group">
                <label className="form-label">装備条件</label>
                <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                  {EQUIPMENT_REQUIREMENT_OPTIONS.map(option => (
                    <label key={option.value} className="checkbox-label" style={{ width: 'auto' }}>
                      <input
                        type="checkbox"
                        checked={formData.equipmentRequirements.includes(option.value)}
                        onChange={() => handleMultiSelectChange('equipmentRequirements', option.value)}
                      />
                      {option.label}
                    </label>
                  ))}
                </div>
                <p className="form-help">この募集で参加者に必須とする装備がある場合はチェックしてください。</p>
              </div>

              <div className="form-group">
                <label className="form-label">参加条件</label>
                <textarea
                  name="requirements"
                  className="form-control"
                  value={formData.requirements}
                  onChange={handleChange}
                  placeholder="例: 山岳経験1年以上、体力に自信がある方"
                  rows="3"
                />
              </div>

              <div className="form-group">
                <label className="form-label">イベント名（任意）</label>
                <input
                  type="text"
                  name="eventName"
                  className="form-control"
                  value={formData.eventName}
                  onChange={handleChange}
                  placeholder="例: 富士山ご来光ツアー 2026夏"
                />
              </div>
            </>
          )}

          <div className="form-group">
            <label className="form-label">詳細説明 *</label>
            <textarea
              name="description"
              className="form-control"
              value={formData.description}
              onChange={handleChange}
              required
              rows="6"
              placeholder={
                recruitmentType === 'group'
                  ? '団体の紹介、活動内容、歓迎するメンバー像などを詳しく記載してください。'
                  : '募集の詳細、計画、注意事項などを記載してください'
              }
            />
          </div>

          <div className="form-group">
            <label className="form-label">募集画像（任意）</label>
            <input
              ref={imageInputRef}
              type="file"
              accept="image/*"
              style={{ display: 'none' }}
              onChange={handleImageChange}
            />
            {recruitmentImagePreview ? (
              <div>
                <img
                  src={recruitmentImagePreview}
                  alt="プレビュー"
                  style={{ width: '100%', maxHeight: '200px', objectFit: 'cover', borderRadius: '8px', marginBottom: '8px' }}
                />
                <button
                  type="button"
                  className="btn btn-outline btn-sm"
                  onClick={() => {
                    setRecruitmentImageFile(null);
                    setRecruitmentImagePreview('');
                    setExistingImageUrl('');
                  }}
                >
                  画像を削除
                </button>
              </div>
            ) : (
              <button type="button" className="btn btn-outline" onClick={() => imageInputRef.current?.click()}>
                画像を選択（10MB以内）
              </button>
            )}
          </div>

          <div className="form-actions">
            <button
              type="submit"
              className="btn btn-primary"
              disabled={loading}
            >
              {loading ? '更新中...' : '募集を更新'}
            </button>

            <Link to={`/recruitments/${id}`} className="btn btn-outline">
              キャンセル
            </Link>
          </div>
        </form>
      </div>
    </div>
  );
}

export default EditRecruitment;
